"use client";

import React from "react";
import { Droplets, Flame, Gauge, PhoneCall, ArrowRight, ShieldAlert, Wrench, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const problems = [{
  id: "dripping-faucets",
  title: "Persistent Faucet Drips & Leaks",
  icon: Droplets,
  badge: "Most Common",
  badgeVariant: "default" as const,
  cause: "Worn-out O-rings, corroded valve seats, or failed internal cartridges.",
  impact: "Can waste hundreds of gallons of water per month and damage vanity bases or countertops.",
  solution: "Replacement of internal seals, valve stems, or complete fixture rebuild."
}, {
  id: "low-pressure",
  title: "Low Water Pressure",
  icon: Gauge,
  badge: "Flow Restriction",
  badgeVariant: "secondary" as const,
  cause: "Mineral deposit buildup inside aerators, blocked cartridge screens, or hidden pipe corrosion.",
  impact: "Slow washing cycles, sputtering water flow, and ineffective rinsing.",
  solution: "Aerator descaling, pressure regulator checks, and supply line flushing."
}, {
  id: "hot-water-issues",
  title: "No Hot Water or Inconsistent Temperature",
  icon: Flame,
  badge: "Water Heater / Valve",
  badgeVariant: "outline" as const,
  cause: "Faulty mixing valves, failing dip tubes in the water heater, or cross-connected supply lines.",
  impact: "Sudden temperature drops during use or lukewarm water from the hot side.",
  solution: "Mixing cartridge replacement, supply valve inspection, and water heater diagnostic."
}, {
  id: "slow-draining",
  title: "Slow-Draining Sink & P-Trap Clogs",
  icon: Wrench,
  badge: "Drainage Issue",
  badgeVariant: "secondary" as const,
  cause: "Accumulated soap scum, hair, grease, or blocked plumbing vent lines.",
  impact: "Standing water in the basin, unpleasant odors, and risk of overflow.",
  solution: "Mechanical snaking, P-trap clearance, and biological drain treatment."
}];
const preventionSteps = ["Clean faucet aerators every 6 months to prevent mineral build-up", "Avoid using harsh chemical cleaners that corrode rubber seals", "Check shut-off valves under the sink twice a year for moisture", "Address minor drips promptly before internal brass threading wears down"];
export default function FaucetProblems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="commonfaucetsinkproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-medium text-xs">
            Diagnostics & Repair
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Common Faucet & Sink Problems
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            From constant nighttime drips to sudden pressure drops, sink and faucet issues waste water and damage cabinetry. Identify your symptom below and let Jonesboro Emergency Plumber resolve it quickly.
          </p>
        </div>

        {/* Problems Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {problems.map((problem, i) => {
          const IconComponent = problem.icon;
          return <motion.div key={problem.id} variants={itemVariants} data-index={i}>
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                          <IconComponent className="size-6" />
                        </div>
                        <Badge variant={problem.badgeVariant}>
                          {problem.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl md:text-2xl font-bold font-default">
                        {problem.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4 text-sm md:text-base">
                      <div>
                        <span className="font-semibold text-foreground block mb-1">
                          Primary Cause:
                        </span>
                        <p className="text-muted-foreground leading-normal">
                          {problem.cause}
                        </p>
                      </div>

                      <div className="p-3 rounded-md bg-muted/60 border border-border/50">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" />
                          <div>
                            <span className="font-medium text-foreground text-xs uppercase tracking-wider block">
                              Impact if ignored:
                            </span>
                            <span className="text-muted-foreground text-xs md:text-sm">
                              {problem.impact}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <span className="font-semibold text-foreground block mb-1">
                          Recommended Fix:
                        </span>
                        <p className="text-muted-foreground leading-normal">
                          {problem.solution}
                        </p>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2">
                    <Button asChild variant="outline" className="w-full justify-between">
                      <Link to="/problems/">
                        <span>Learn Repair Options</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Diagnostic Checklist & Emergency Action Box */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Preventative Tips Column */}
          <div className="lg:col-span-7 bg-muted/40 border border-border rounded-xl p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="size-5 text-primary" />
                <h3 className="font-default text-lg md:text-xl font-bold text-foreground">
                  Fixture Maintenance Checklist
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Small preventative habits protect your sinks, garbage disposals, and water lines from unexpected breakdown:
              </p>
              <ul className="space-y-3.5">
                {preventionSteps.map((step, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-foreground">
                      {step}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm font-medium text-muted-foreground">
                Experiencing persistent fixture trouble?
              </span>
              <Button asChild variant="secondary" size="sm">
                <Link to="/services/">
                  <span>View All Services</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Direct Urgent Response Callout */}
          <div className="lg:col-span-5 bg-primary text-primary-foreground rounded-xl p-6 md:p-8 flex flex-col justify-between shadow-md">
            <div>
              <Badge className="bg-accent text-accent-foreground font-semibold mb-4 border-transparent">
                24/7 Rapid Response
              </Badge>
              <h3 className="font-default text-2xl md:text-3xl font-bold mb-3 text-primary-foreground">
                Need Fast Sink or Faucet Repair in Jonesboro?
              </h3>
              <p className="text-primary-foreground/90 text-sm md:text-base leading-relaxed mb-6">
                Major leaks underneath your basin can quickly cause rot and mold in base cabinets. Our licensed plumbers arrive promptly with fully stocked trucks.
              </p>
            </div>

            <div className="space-y-3">
              <Button asChild size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-sm">
                <Link to="/contact/">
                  <PhoneCall className="size-4" />
                  <span>Request Immediate Service</span>
                </Link>
              </Button>
              <p className="text-center text-xs text-primary-foreground/75">
                Jonesboro, GA and surrounding communities
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}